import React, { useState, useEffect } from "react";
import Webcam from "react-webcam";
import { AlertTriangle } from "lucide-react";
import default_camera_bg from "@/assets/interview/default_camera_bg.png";
import eLogo from "@/assets/branding/eLogo.svg";

interface MobileWebCamCheckProps {
  onConnectWithInterview: (selectedCamera: string) => Promise<void> | void;
  isSocketReady?: boolean;
  status?: string;
  statusError?: string | null;
  isConnecting?: boolean;
  onReconnect?: () => void;
}

/*
  ------------------------------------------------------
  Mobile preflight guides permissions and clean reconnect attempts
  ------------------------------------------------------
*/
const MobileWebCamCheck: React.FC<MobileWebCamCheckProps> = ({
  onConnectWithInterview,
  status,
  isConnecting = false,
}) => {
  const [isSharingWebcam, setIsSharingWebcam] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isLoadingPermission, setIsLoadingPermission] = useState(true);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const webcamRef = React.useRef<Webcam>(null);
  const showNetworkWarning = status === "error";

  const isEstablishingSecureConnection = [
    "creating-offer",
    "waiting-for-offer",
    "signaling",
    "connecting",
  ].includes(status ?? "");
  const isBusyConnecting = isConnecting || isEstablishingSecureConnection;

  useEffect(() => {
    requestCameraPermission();
  }, []);

  const requestCameraPermission = async () => {
    setIsLoadingPermission(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });

      setHasPermission(true);
      setPermissionDenied(false);

      // Get available cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      setCameras(videoDevices);

      // Select first camera by default and auto-start preview
      if (videoDevices.length > 0 && videoDevices[0]) {
        const firstCameraId = videoDevices[0].deviceId;
        setSelectedCamera((prev) => prev || firstCameraId);
      }

      // Stop the stream immediately, we just needed permission
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Camera permission denied:", error);
      setHasPermission(false);
      setPermissionDenied(true);
    } finally {
      setIsLoadingPermission(false);
    }
  };

  useEffect(() => {
    if (hasPermission && selectedCamera && !isSharingWebcam) {
      setIsSharingWebcam(true);
    }
  }, [hasPermission, selectedCamera, isSharingWebcam]);

  const handleConnectClick = async () => {
    if (!selectedCamera) return;
    try {
      await onConnectWithInterview(selectedCamera);
      setIsSharingWebcam(false);
    } catch (error) {
      console.error("Failed to connect interview", error);
    }
  };

  return (
    <div className="w-full overflow-hidden flex flex-col min-h-[calc(100vh-64px)]">
      {/* Header */}
      <div className="flex items-center justify-center gap-2 mb-6 flex-shrink-0">
        <img src={eLogo} alt="EmployAbility Logo" className="w-6 h-6" />
        <span className="text-lg font-ubuntu font-bold text-[#001630]">
          Employ<span className="text-[#0AD472]">Ability.AI</span>
        </span>
      </div>

      {/* Permission Status */}
      {isLoadingPermission && (
        <div className="mb-4 p-3 bg-white border border-gray-300 rounded-lg text-center flex-shrink-0">
          <p className="text-gray-800 text-sm">
            Requesting camera permission...
          </p>
        </div>
      )}

      {permissionDenied && !isLoadingPermission && (
        <div className="mb-4 p-3 bg-white border border-gray-300 rounded-lg flex-shrink-0">
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-gray-800 w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-gray-800 font-ubuntu-sans font-semibold text-sm">
                Camera Permission Required
              </p>
            </div>
            <div className="flex gap-2">
              <div className="flex flex-col gap-2 flex-1">
                <p className="text-gray-600 text-xs">
                  Please allow camera access to continue. Check your browser
                  settings if the permission prompt didn't appear.
                </p>
                <button
                  onClick={requestCameraPermission}
                  className="px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs"
                >
                  Request Permission Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Webcam Section */}
      <div className="mb-4 flex-1 flex flex-col min-h-0">
        <div className="p-4 bg-white rounded-lg border border-gray-300 overflow-hidden flex-1 flex flex-col min-h-0">
          <h2 className="text-base font-ubuntu-sans font-semibold text-gray-800 mb-3 flex-shrink-0">
            Workspace Camera Preview
          </h2>

          <div className="space-y-3 flex-1 flex flex-col min-h-0">
            {/* Camera Selection Dropdown */}
            {hasPermission && cameras.length > 0 && (
              <div className="mb-3 flex-shrink-0">
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Select Camera
                </label>
                <select
                  value={selectedCamera}
                  onChange={(e) => setSelectedCamera(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  {cameras.map((camera) => (
                    <option key={camera.deviceId} value={camera.deviceId}>
                      {camera.label ||
                        `Camera ${camera.deviceId.substring(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Webcam Preview */}
            <div
              className="bg-black rounded-lg overflow-hidden w-full flex-1 min-h-0"
              style={{
                aspectRatio: "16/9",
                minHeight: "450px",
              }}
            >
              {isSharingWebcam && selectedCamera ? (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  videoConstraints={{
                    deviceId: selectedCamera,
                    width: 1280,
                    height: 720,
                    facingMode: "user",
                  }}
                  className="w-full h-full object-cover"
                  style={{
                    transform: "scaleX(-1)",
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src={default_camera_bg}
                    alt="default camera background"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            {/* Control Buttons */}
          </div>
        </div>
      </div>

      {/* Connect Button */}
      <div className="flex flex-col gap-2 flex-shrink-0 mt-auto">
        {/* <p className="text-xs font-ubuntu-sans text-gray-800 text-left">{connectionLabel}</p> */}
        <p className="text-xs italic text-amber-600 text-left">
          Ensure both your phone and laptop are on the same Wi-Fi before
          connecting.
        </p>
        <div className="flex justify-center">
          {/*
          Keep the button disabled to prevent double-submits, but when connecting
          show it in the active style with a spinner instead of the disabled style.
        */}
          <button
            onClick={handleConnectClick}
            disabled={
              !isSharingWebcam ||
              !hasPermission ||
              !selectedCamera ||
              isBusyConnecting ||
              showNetworkWarning
            }
            className={`px-6 py-2.5 text-sm font-ubuntu-sans rounded-lg transition-colors font-medium w-full inline-flex items-center justify-center gap-2 ${
              isBusyConnecting
                ? "bg-gray-800 text-white cursor-wait"
                : "bg-gray-800 text-white hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            }`}
          >
            {isBusyConnecting && (
              <span
                className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"
                aria-hidden="true"
              />
            )}
            {isBusyConnecting
              ? "Connecting..."
              : !isSharingWebcam
              ? "Enable Webcam to Continue"
              : "Connect with Interview"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileWebCamCheck;


